/**
 * Turns the balance walk into the numbers Closing Stock and Availability render: the additive
 * per-key components, their per-period differences, and the integer breakdowns the hovers print.
 *
 * The per-period parts satisfy, exactly:
 *   closing = opening + incoming + returns - demand + lostSales
 * so lost sales is the reconciling residual: demand that could not deplete stock because stock was
 * already at zero. Components are cumulative and additive at any grain, so summing across keys then
 * differencing equals differencing then summing.
 *
 * The walk (`./walk`) and the demand curve (`./curve`) are module internals, guarded by a lint
 * rule; surfaces reach for `projectPeriods`.
 */
import { demandAheadOfToday, getElapsedWeekDemand } from "./curve"
import { reportProjectionInvariants } from "./invariants"
import { calculateStockLevelAtWeeks } from "./walk"
import { sumBy } from "~/utils/tsUtils"

import type {
  Availability,
  ClosingStockBreakdown,
  PeriodAvailability,
  ProjectionKeyInputs,
  StockLevelComponents,
} from "./types"

/**
 * Projects one `{sku, warehouse}` across the requested period ends.
 *
 * `weekIdx` are period *ends* in fractional weeks from the current Monday. Ends inside the current
 * week charge the week's remainder across the days still to come (`getDemandConsumedByWeek`), so
 * week 0 reads back the snapshot itself with no demand and no residual.
 */
export function projectKeyComponents(
  input: ProjectionKeyInputs,
  weekIdx: number[],
  stockWeek: number,
): { levels: number[]; components: StockLevelComponents[] } {
  const { forecasts, incomingOrders, forecastedReturns, salesWeekToDate } = input
  // Oversold SKUs carry a negative on-hand, and the walk floors every level it returns at 0. Passed
  // through raw, the opening breaks the residual identity in both directions: on the drain path the
  // floored closing sits above `opening + incoming + returns`, making lost sales exceed demand
  // (agent_provocateur's APR000820-001001 printed -163%), while the post-restock path drops the
  // opening entirely and makes the residual negative. The walk's own floor is the consistent
  // reading: nothing on hand.
  const opening = Math.max(input.opening, 0)

  const levels = calculateStockLevelAtWeeks(
    weekIdx,
    forecasts,
    [...incomingOrders, ...forecastedReturns],
    opening,
    stockWeek,
    salesWeekToDate,
  )
  const elapsedWeekDemand = getElapsedWeekDemand(forecasts, stockWeek, salesWeekToDate)

  const components = weekIdx.map(
    (w, i): StockLevelComponents => ({
      opening,
      incoming: sumBy(
        incomingOrders.filter((o) => o.weekDiff <= w),
        "quantity",
      ),
      overdueIncoming: sumBy(
        incomingOrders.filter((o) => o.overdue === true && o.weekDiff <= w),
        "quantity",
      ),
      returns: sumBy(
        forecastedReturns.filter((r) => r.weekDiff <= w),
        "quantity",
      ),
      // Same curve the levels charge, so the residual below cannot inherit a schedule mismatch.
      demand: Math.max(demandAheadOfToday(forecasts, w, stockWeek, salesWeekToDate), 0),
      weekForecast: forecasts[0] ?? 0,
      weekDemandCovered: elapsedWeekDemand,
      weekSales: salesWeekToDate ?? 0,
      hasSkuAdjustedDemand: input.hasSkuAdjustedDemand,
      weekDemandPlan: 0,
      closing: levels[i] ?? 0,
    }),
  )
  reportProjectionInvariants(input, derivePeriodComponentSeries(components))

  return { levels, components }
}

const emptyComponents = (): StockLevelComponents => ({
  opening: 0,
  incoming: 0,
  overdueIncoming: 0,
  returns: 0,
  demand: 0,
  weekForecast: 0,
  weekDemandCovered: 0,
  weekSales: 0,
  hasSkuAdjustedDemand: false,
  weekDemandPlan: 0,
  closing: 0,
})

/** An all-zero grid, for a caller that renders one row per period regardless. */
export const emptyComponentGrid = (periodCount: number): StockLevelComponents[] =>
  Array.from({ length: periodCount }, emptyComponents)

/** Sums one key's levels into an accumulator, which it owns — `add` is never captured. */
export function accumulateLevels(into: number[] | undefined, add: number[]): number[] {
  if (!into) return [...add]
  for (let i = 0; i < add.length; i++) {
    into[i] = (add[i] ?? 0) + (into[i] ?? 0)
  }
  return into
}

/**
 * Sums one key's components into an accumulator. The accumulator is always freshly owned: seeding
 * it with `add` would alias the caller's per-key array and turn the first key's row into the
 * running total.
 */
export function accumulateComponents(
  into: StockLevelComponents[] | undefined,
  add: StockLevelComponents[],
): StockLevelComponents[] {
  if (!into) return add.map((c) => ({ ...c }))
  for (let i = 0; i < add.length; i++) {
    const target = into[i]
    const source = add[i]
    if (!target || !source) continue
    target.opening += source.opening
    target.incoming += source.incoming
    target.overdueIncoming += source.overdueIncoming
    target.returns += source.returns
    target.demand += source.demand
    target.weekForecast += source.weekForecast
    target.weekDemandCovered += source.weekDemandCovered
    target.weekSales += source.weekSales
    target.hasSkuAdjustedDemand = target.hasSkuAdjustedDemand || source.hasSkuAdjustedDemand
    target.weekDemandPlan += source.weekDemandPlan
    target.closing += source.closing
  }
  return into
}

/** Projects a set of keys onto one period grid, summing each key into its aggregate row. */
export function projectPeriods(
  entries: Iterable<{ aggregateKey: string; inputs: ProjectionKeyInputs }>,
  weekIdx: number[],
  stockWeek: number,
): {
  levels: Record<string, number[]>
  components: Record<string, StockLevelComponents[]>
  forecastWeeks: Record<string, number>
} {
  const levels: Record<string, number[]> = {}
  const components: Record<string, StockLevelComponents[]> = {}
  const forecastWeeks: Record<string, number> = {}

  for (const { aggregateKey, inputs } of entries) {
    forecastWeeks[aggregateKey] = Math.max(
      forecastWeeks[aggregateKey] ?? 0,
      inputs.forecasts.length,
    )
    const projected = projectKeyComponents(inputs, weekIdx, stockWeek)
    levels[aggregateKey] = accumulateLevels(levels[aggregateKey], projected.levels)
    components[aggregateKey] = accumulateComponents(components[aggregateKey], projected.components)
  }

  return { levels, components, forecastWeeks }
}

/**
 * Field-wise total across every aggregate row, for a request-level row. `undefined` when no key
 * reached the grid, which callers distinguish from a genuine all-zero total.
 */
export function totalComponents(
  components: Record<string, StockLevelComponents[]>,
): StockLevelComponents[] | undefined {
  let total: StockLevelComponents[] | undefined
  for (const comps of Object.values(components)) {
    total = accumulateComponents(total, comps)
  }
  return total
}

/** Cumulative (today → period end) additive parts of a projected stock level. */
export type CumulativeStockComponents = {
  opening: number
  incoming: number
  returns: number
  demand: number
  closing: number
}

/**
 * Parts attributable to a single period, additive across keys. Ratios never live here — ask
 * `availabilityOf` for one once the parts are totalled. `PeriodBreakdown` is the same shape after
 * rounding.
 */
export type PeriodStockComponents = {
  opening: number
  incoming: number
  returns: number
  demand: number
  lostSales: number
  closing: number
}

export type PeriodBreakdown = PeriodStockComponents

/**
 * A period's parts widened with the cumulative-from-today facts only the first cell can explain:
 * which of its arrivals were already overdue, and how much of this week's forecast week-to-date
 * sales already covered.
 */
export type PeriodParts = PeriodStockComponents & {
  overdueIncoming: number
  weekSales: number
  weekDemandCovered: number
}

/**
 * Everything a cell's hover prints, reconciled once. `previous` is the prior period's chain, for a
 * caller that has one worth showing.
 */
export type AvailabilityPeriodBreakdown = {
  period: PeriodBreakdown | undefined
  previous: PeriodBreakdown | undefined
  /** Arrivals in this period that were already overdue. Clamped to `period.incoming`. */
  pastDueIncoming: number
  /** How much of the current week's demand week-to-date sales already covered. */
  weekSalesCounted: number
  /** Whether that deduction is measured sales or a prorated share of the forecast. */
  weekSalesKnown: boolean
  /** The period opens on today's stock snapshot rather than a prior period's closing. */
  isFirstPeriod: boolean
}

/**
 * One period's parts from two consecutive cumulative snapshots. `previous` is undefined for
 * the first period, whose opening is the stock snapshot itself.
 */
export function derivePeriodComponents(
  current: CumulativeStockComponents,
  previous: CumulativeStockComponents | undefined,
): PeriodStockComponents {
  const opening = previous ? previous.closing : current.opening
  const incoming = current.incoming - (previous?.incoming ?? 0)
  const returns = current.returns - (previous?.returns ?? 0)
  // 3dp-rounded cumulative arrays dip by 0.001 between weeks; a negative period demand is never demand.
  const demand = Math.max(current.demand - (previous?.demand ?? 0), 0)
  const lostSales = current.closing - opening - incoming - returns + demand

  return {
    opening,
    incoming,
    returns,
    demand,
    lostSales,
    closing: current.closing,
  }
}

export function derivePeriodComponentSeries(
  cumulative: CumulativeStockComponents[],
): PeriodStockComponents[] {
  return cumulative.map((current, i) => derivePeriodComponents(current, cumulative[i - 1]))
}

/** Field-wise sum of period parts, for aggregating a row's keys before rounding them. */
export function sumPeriodComponents(parts: PeriodStockComponents[]): PeriodStockComponents {
  const total = { opening: 0, incoming: 0, returns: 0, demand: 0, lostSales: 0, closing: 0 }
  for (const p of parts) {
    total.opening += p.opening
    total.incoming += p.incoming
    total.returns += p.returns
    total.demand += p.demand
    total.lostSales += p.lostSales
    total.closing += p.closing
  }
  return total
}

/**
 * Rounds a chain to integers, with demand as the balancing term so the identity survives:
 *   opening + incoming + returns - demand + lostSales === closing
 * The raw `demand` is not read: rounding each part on its own leaves the printed chain off by a
 * unit or two, so demand is recomputed from the rest.
 */
export function finalizePeriodBreakdown(p: PeriodStockComponents): PeriodBreakdown {
  const opening = Math.round(p.opening)
  const incoming = Math.round(p.incoming)
  const returns = Math.round(p.returns)
  const closing = Math.round(p.closing)
  let lostSales = Math.max(0, Math.round(p.lostSales))
  let demand = opening + incoming + returns + lostSales - closing
  if (demand < 0) {
    lostSales -= demand
    demand = 0
  }
  return { opening, incoming, returns, demand, lostSales, closing }
}

/**
 * The cumulative row's integer breakdown. Same balancing as `finalizePeriodBreakdown` — which owns
 * the arithmetic — under the field names the Closing Stock hover renders, plus the current-week
 * split. `StockLevelComponents` carries no residual, so it comes off the identity.
 */
export const finalizeBreakdown = (c: StockLevelComponents): ClosingStockBreakdown => {
  const chain = finalizePeriodBreakdown({
    opening: c.opening,
    incoming: c.incoming,
    returns: c.returns,
    demand: c.demand,
    lostSales: c.closing - (c.opening + c.incoming + c.returns - c.demand),
    closing: c.closing,
  })
  const remainingWeekRaw = Math.max(c.weekForecast - c.weekDemandCovered, 0)
  return {
    currentStock: chain.opening,
    incoming: chain.incoming,
    overdueIncoming: Math.min(Math.round(c.overdueIncoming), chain.incoming),
    expectedReturns: chain.returns,
    expectedDemand: chain.demand,
    currentWeekForecast: Math.round(c.weekForecast),
    currentWeekSales: Math.round(c.weekSales),
    currentWeekSalesCounted: Math.round(c.weekDemandCovered),
    laterWeeksForecast: Math.round(Math.max(c.demand - remainingWeekRaw, 0)),
    hasSkuAdjustedDemand: c.hasSkuAdjustedDemand,
    currentWeekDemandPlan: c.weekDemandPlan,
    lostSales: chain.lostSales,
    closing: chain.closing,
  }
}

/**
 * Sums a row's keys and reconciles once, so every equation a hover prints closes exactly.
 *
 * Arrivals are a superset of overdue arrivals, so the clamp matters: rounding the two independently
 * could print "includes 6 overdue" beneath an incoming of 5. Only the first period contains the
 * part-elapsed week, so only it carries the deduction facts.
 */
export function periodBreakdownForKeys(
  parts: PeriodParts[],
  previousParts: PeriodParts[] | undefined,
  isFirstPeriod: boolean,
): AvailabilityPeriodBreakdown {
  const period = parts.length ? finalizePeriodBreakdown(sumPeriodComponents(parts)) : undefined
  const previous = previousParts?.length
    ? finalizePeriodBreakdown(sumPeriodComponents(previousParts))
    : undefined

  const sumField = (field: "overdueIncoming" | "weekSales" | "weekDemandCovered") =>
    parts.reduce((acc, p) => acc + p[field], 0)

  return {
    period,
    previous,
    pastDueIncoming: isFirstPeriod
      ? Math.min(Math.round(sumField("overdueIncoming")), period?.incoming ?? 0)
      : 0,
    weekSalesCounted: isFirstPeriod ? Math.round(sumField("weekDemandCovered")) : 0,
    weekSalesKnown: sumField("weekSales") > 0,
    isFirstPeriod,
  }
}

/**
 * A period past the forecast horizon differences two cumulative curve reads that should cancel and
 * gets ~1e-15 units of float dust instead of a clean zero — of either sign, since `Math.max(.., 0)`
 * on the cumulative value does not make the difference monotonic. An exact `=== 0` guard lets that
 * through and divides dust by dust: on agent_provocateur's APM1297001001, 2027-07 summed +1.8e-15
 * of demand against dust the per-key `max(lostSales, 0)` had already stripped the negative half
 * from, and printed -100%; 2027-09 printed 100% off -4.4e-16. Demand is in units — a millionth of
 * one is not demand.
 */
const DEMAND_EPSILON = 1e-6

/**
 * The same dust lands in the residual: a period with nothing lost differences to ±1e-12 units,
 * which the `max(.., 0)` clamp only strips on the negative side. Positive dust left a ratio of
 * 0.99999999999998 on organic_basics' OB420033-Black, 2027-01 — printed as 100%, but a grid filter
 * on "equals 100" tested the raw value and dropped the row. Snapped to a clean 0 (and, mirrored,
 * to a clean `demand`) so the ends of the scale are exact.
 */
const snapResidual = (lostSales: number, demand: number): number => {
  if (lostSales < DEMAND_EPSILON) return 0
  if (lostSales > demand - DEMAND_EPSILON) return demand
  return lostSales
}

const ratioOf = (demand: number, lostSales: number): Availability =>
  demand < DEMAND_EPSILON
    ? { kind: "noDemand" }
    : // Clamped to [0, 1]: the residual can overshoot demand by float noise, and `toFixed(0)`
      // prints -1e-13 as "-0%".
      { kind: "ratio", value: 1 - snapResidual(lostSales, demand) / demand }

/**
 * The one place a set of residual pairs becomes availability. Sums the residual and the demand
 * before dividing; a mean of per-pair ratios would not compose.
 */
export function availabilityOf(pairs: PeriodAvailability[]): Availability {
  let demand = 0
  let lostSales = 0
  for (const p of pairs) {
    demand += p.demand
    lostSales += p.lostSales
  }
  return ratioOf(demand, lostSales)
}

/**
 * Availability for one grid cell, across the cell's keys. Keys the projection never reached
 * contribute nothing rather than a zero-demand row.
 */
export function availabilityForKeys(
  byKey: Map<string, PeriodAvailability> | undefined,
  keyIds: string[],
): { demand: number; lostSales: number; availability: Availability; hasData: boolean } {
  let demand = 0
  let lostSales = 0
  let found = 0
  for (const id of keyIds) {
    const row = byKey?.get(id)
    if (row === undefined) continue
    found++
    demand += row.demand
    lostSales += row.lostSales
  }
  // `hasData` separates "this key had no demand" from "this key was never projected"; the
  // availability result collapses both to `noDemand`, since neither is a number to report.
  return { demand, lostSales, availability: ratioOf(demand, lostSales), hasData: found > 0 }
}
